/**
 * ===============================================================
 *  📄  ملف المحتوى القانوني الموحّد — Single Source of Truth
 * ===============================================================
 *
 *  هذا الملف الوحيد يتحكم بكل نصوص الصفحات القانونية الثلاث:
 *  - سياسة الخصوصية   (PrivacyPolicy.js)
 *  - شروط الاستخدام   (TermsOfService.js)
 *  - حذف البيانات     (DataDeletion.js)
 *
 *  ✅ عدّل النص من هنا فقط — والتطبيق سيعرض التغييرات تلقائياً
 *      في الصفحات الثلاث دون الحاجة لتعديل أي شيء آخر.
 *
 *  🌐  كل قسم يحتوي نسختين: ar (عربي) + en (إنجليزي)
 *  📅  حدّث حقل lastUpdated عند أي تعديل جوهري.
 * ===============================================================
 */

export const LEGAL_CONTENT = {
  // آخر تحديث يظهر أسفل كل صفحة قانونية
  lastUpdated: {
    ar: 'آخر تحديث: يوليو 2026',
    en: 'Last updated: July 2026',
  },

  // ─────────────────────────────────────────────────────────────
  // 1) سياسة الخصوصية / Privacy Policy
  // ─────────────────────────────────────────────────────────────
  privacy: {
    intro: {
      ar: 'في CloudGM، نأخذ خصوصيتك على محمل الجد. تشرح سياسة الخصوصية هذه كيفية جمع معلوماتك الشخصية واستخدامها وحمايتها.',
      en: 'At CloudGM, we take your privacy seriously. This privacy policy explains how we collect, use, and protect your personal information.',
    },
    sections: [
      {
        iconKey: 'Lock',
        ar: { title: 'جمع المعلومات', content: 'نقوم بجمع المعلومات التي تقدمها لنا مباشرة عند التسجيل أو استخدام خدماتنا، بما في ذلك الاسم والبريد الإلكتروني ورقم الهاتف والمعلومات الأخرى اللازمة لتقديم الخدمات.' },
        en: { title: 'Information Collection', content: 'We collect information you provide directly when registering or using our services, including name, email, phone number, and other information necessary to provide services.' },
      },
      {
        iconKey: 'Eye',
        ar: { title: 'استخدام المعلومات', content: 'نستخدم المعلومات المجمعة لتقديم الخدمات وتحسينها، والتواصل معك بشأن حسابك والخدمات، وإرسال التحديثات والعروض ذات الصلة، ومنع الاحتيال وضمان الأمان.' },
        en: { title: 'Use of Information', content: 'We use collected information to provide and improve services, communicate with you about your account and services, send relevant updates and offers, and prevent fraud and ensure security.' },
      },
      {
        iconKey: 'Server',
        ar: { title: 'حماية البيانات', content: 'نطبق تدابير أمنية صارمة لحماية بياناتك من الوصول غير المصرح به والاستخدام الخاطئ. نستخدم تشفير SSL وجدران الحماية وإجراءات التحكم في الوصول لضمان أمان معلوماتك.' },
        en: { title: 'Data Protection', content: 'We apply strict security measures to protect your data from unauthorized access and misuse. We use SSL encryption, firewalls, and access control procedures to ensure your information security.' },
      },
      {
        iconKey: 'Bell',
        ar: { title: 'مشاركة المعلومات', content: 'لا نبيع أو نؤجر معلوماتك الشخصية لأطراف ثالثة. قد نشارك المعلومات مع مقدمي الخدمات الموثوقين الذين يساعدوننا في تشغيل منصتنا، أو عند الطلب القانوني من السلطات المختصة.' },
        en: { title: 'Information Sharing', content: 'We do not sell or rent your personal information to third parties. We may share information with trusted service providers who help us operate our platform, or upon legal request from competent authorities.' },
      },
      {
        iconKey: 'UserCheck',
        ar: { title: 'حقوقك', content: 'لديك الحق في الوصول إلى بياناتك الشخصية وتصحيحها أو حذفها. يمكنك أيضاً إلغاء الاشتراك في الاتصالات التسويقية في أي وقت عن طريق التواصل معنا أو استخدام رابط إلغاء الاشتراك في رسائلنا الإلكترونية.' },
        en: { title: 'Your Rights', content: 'You have the right to access, correct, or delete your personal data. You can also unsubscribe from marketing communications at any time by contacting us or using the unsubscribe link in our emails.' },
      },
      {
        iconKey: 'FileText',
        ar: { title: 'تحديثات السياسة', content: 'قد نقوم بتحديث سياسة الخصوصية هذه من وقت لآخر. سنخطرك بأي تغييرات جوهرية عن طريق نشر السياسة الجديدة على موقعنا وإرسال إشعار إذا كان التغيير كبيراً.' },
        en: { title: 'Policy Updates', content: 'We may update this privacy policy from time to time. We will notify you of any material changes by posting the new policy on our website and sending a notice if the change is significant.' },
      },
    ],
    dataDeletionCta: {
      ar: { text: 'هل تريد حذف حسابك وبياناتك؟', link: 'صفحة حذف بيانات المستخدم ←' },
      en: { text: 'Want to delete your account and data?', link: 'User Data Deletion page →' },
    },
    contact: {
      ar: { heading: 'تواصل معنا', line: 'إذا كانت لديك أي أسئلة حول سياسة الخصوصية، يرجى التواصل معنا على: ' },
      en: { heading: 'Contact Us', line: 'If you have any questions about our privacy policy, please contact us at: ' },
    },
  },

  // ─────────────────────────────────────────────────────────────
  // 2) شروط الاستخدام / Terms of Service
  // ─────────────────────────────────────────────────────────────
  terms: {
    warning: {
      ar: 'يرجى قراءة هذه الشروط والأحكام بعناية قبل استخدام خدماتنا. باستخدامك لمنصة CloudGM، فإنك توافق على الالتزام بجميع الشروط المذكورة أدناه.',
      en: 'Please read these terms and conditions carefully before using our services. By using the CloudGM platform, you agree to comply with all the terms mentioned below.',
    },
    sections: [
      {
        ar: { title: 'قبول الشروط', content: 'باستخدام منصة CloudGM وخدماتها، فإنك توافق على الالتزام بهذه الشروط والأحكام. إذا كنت لا توافق على أي جزء من هذه الشروط، يرجى عدم استخدام خدماتنا.' },
        en: { title: 'Acceptance of Terms', content: 'By using the CloudGM platform and its services, you agree to be bound by these terms and conditions. If you do not agree to any part of these terms, please do not use our services.' },
      },
      {
        ar: { title: 'الخدمات المقدمة', content: 'تقدم CloudGM مجموعة من الخدمات تشمل: تخليص المعاملات الحكومية، خدمات التأشيرات والإقامة، الترجمة القانونية، تأسيس الشركات، سوق الأرقام واللوحات المميزة، العقارات، والتجارة الإلكترونية.' },
        en: { title: 'Services Provided', content: 'CloudGM offers a range of services including: government transaction processing, visa and residency services, legal translation, company formation, premium numbers and plates marketplace, real estate, and e-commerce.' },
      },
      {
        ar: { title: 'حساب المستخدم', content: 'أنت مسؤول عن الحفاظ على سرية معلومات حسابك وكلمة المرور الخاصة بك. يجب أن تكون جميع المعلومات المقدمة عند التسجيل صحيحة ودقيقة ومحدثة. يحق لنا تعليق أو إنهاء حسابك إذا كانت المعلومات المقدمة غير صحيحة.' },
        en: { title: 'User Account', content: 'You are responsible for maintaining the confidentiality of your account information and password. All information provided during registration must be true, accurate, and current. We reserve the right to suspend or terminate your account if the information provided is incorrect.' },
      },
      {
        ar: { title: 'الأسعار والدفع', content: 'جميع الأسعار المعروضة بالدرهم الإماراتي ما لم يُذكر خلاف ذلك. قد تتغير الأسعار دون إشعار مسبق. نقبل طرق الدفع المختلفة وفقاً لما هو متاح على المنصة. جميع المدفوعات نهائية ما لم يُنص على خلاف ذلك.' },
        en: { title: 'Pricing and Payment', content: 'All prices are displayed in UAE Dirhams unless otherwise stated. Prices may change without prior notice. We accept various payment methods as available on the platform. All payments are final unless otherwise stated.' },
      },
      {
        ar: { title: 'حقوق الملكية الفكرية', content: 'جميع المحتويات والعلامات التجارية والتصاميم على منصتنا مملوكة لـ CloudGM أو مرخصة لها. لا يجوز نسخ أو توزيع أي محتوى دون إذن كتابي مسبق.' },
        en: { title: 'Intellectual Property Rights', content: 'All content, trademarks, and designs on our platform are owned by or licensed to CloudGM. No content may be copied or distributed without prior written permission.' },
      },
      {
        ar: { title: 'إخلاء المسؤولية', content: 'نسعى لتقديم أفضل الخدمات، لكننا لا نضمن أن الخدمات ستكون خالية من الأخطاء أو الانقطاعات. نحن غير مسؤولين عن أي أضرار غير مباشرة ناتجة عن استخدام خدماتنا.' },
        en: { title: 'Disclaimer', content: 'We strive to provide the best services, but we do not guarantee that services will be error-free or uninterrupted. We are not responsible for any indirect damages resulting from the use of our services.' },
      },
      {
        ar: { title: 'القانون الحاكم', content: 'تخضع هذه الشروط وتُفسر وفقاً لقوانين دولة الإمارات العربية المتحدة. أي نزاع ينشأ عن هذه الشروط يخضع للاختصاص القضائي لمحاكم الإمارات.' },
        en: { title: 'Governing Law', content: 'These terms are governed by and construed in accordance with the laws of the United Arab Emirates. Any dispute arising from these terms is subject to the jurisdiction of UAE courts.' },
      },
      {
        ar: { title: 'التعديلات', content: 'نحتفظ بالحق في تعديل هذه الشروط في أي وقت. سيتم إخطارك بأي تغييرات جوهرية عبر البريد الإلكتروني أو من خلال إشعار على المنصة. استمرارك في استخدام الخدمات بعد التعديلات يعني قبولك للشروط الجديدة.' },
        en: { title: 'Amendments', content: 'We reserve the right to modify these terms at any time. You will be notified of any material changes via email or through a notice on the platform. Your continued use of services after modifications means acceptance of the new terms.' },
      },
    ],
    agreement: {
      ar: 'باستخدامك للمنصة، فإنك توافق على هذه الشروط',
      en: 'By using the platform, you agree to these terms',
    },
    contact: {
      ar: 'للاستفسارات حول الشروط والأحكام، تواصل معنا على: ',
      en: 'For inquiries about terms and conditions, contact us at: ',
    },
  },

  // ─────────────────────────────────────────────────────────────
  // 3) حذف البيانات / Data Deletion
  // ─────────────────────────────────────────────────────────────
  dataDeletion: {
    title: {
      ar: 'حذف بيانات المستخدم',
      en: 'User Data Deletion',
    },
    subtitle: {
      ar: 'كيف تطلب حذف حسابك وبياناتك نهائياً من CloudGM',
      en: 'How to request permanent deletion of your account and data from CloudGM',
    },
    intro: {
      ar: 'نحترم حقك الكامل في التحكم ببياناتك. يمكنك في أي وقت طلب حذف حسابك وجميع بياناتك الشخصية من منصة CloudGM نهائياً باتباع الخطوات التالية:',
      en: 'We fully respect your right to control your data. You can request the permanent deletion of your account and all your personal data from the CloudGM platform at any time by following these steps:',
    },
    steps: [
      {
        iconKey: 'Mail',
        ar: { title: 'أرسل طلب الحذف', content: 'أرسل بريداً إلكترونياً إلى {EMAIL} بعنوان "طلب حذف البيانات" من نفس البريد المسجل في حسابك، أو تواصل معنا عبر الهاتف {PHONE}.' },
        en: { title: 'Submit a Deletion Request', content: 'Send an email to {EMAIL} with the subject "Data Deletion Request" from the same email registered on your account, or contact us by phone at {PHONE}.' },
      },
      {
        iconKey: 'ShieldCheck',
        ar: { title: 'التحقق من الهوية', content: 'سنتحقق من ملكيتك للحساب للتأكد من أن طلب الحذف صادر منك شخصياً، حمايةً لبياناتك من أي طلبات احتيالية.' },
        en: { title: 'Identity Verification', content: 'We will verify your account ownership to make sure the deletion request comes from you personally, protecting your data from fraudulent requests.' },
      },
      {
        iconKey: 'Database',
        ar: { title: 'حذف البيانات', content: 'سيتم حذف حسابك وجميع بياناتك الشخصية نهائياً: الملف الشخصي، الإعلانات، الطلبات، المفضلة، سجل المحادثات مع المساعد الذكي، والمستندات المرفوعة.' },
        en: { title: 'Data Deletion', content: 'Your account and all personal data will be permanently deleted: profile, ads, orders, favorites, AI assistant chat history, and uploaded documents.' },
      },
      {
        iconKey: 'Clock',
        ar: { title: 'مدة التنفيذ', content: 'يتم تنفيذ طلبات الحذف خلال 30 يوماً كحد أقصى من تاريخ التحقق من الطلب. ستصلك رسالة تأكيد عند اكتمال الحذف.' },
        en: { title: 'Processing Time', content: 'Deletion requests are processed within a maximum of 30 days from the date the request is verified. You will receive a confirmation message once the deletion is complete.' },
      },
    ],
    ctaButton: {
      ar: 'إرسال طلب حذف البيانات الآن',
      en: 'Send a Data Deletion Request Now',
    },
    emailSubject: {
      ar: 'طلب حذف البيانات - Data Deletion Request',
      en: 'Data Deletion Request',
    },
    emailBody: {
      ar: 'أرغب في حذف حسابي وجميع بياناتي الشخصية من منصة CloudGM نهائياً.\n\nالبريد المسجل في الحساب: ',
      en: 'I would like to permanently delete my account and all my personal data from the CloudGM platform.\n\nRegistered account email: ',
    },
    retainedNote: {
      ar: 'قد نحتفظ ببعض البيانات التي يفرض القانون الإماراتي الاحتفاظ بها (مثل سجلات الفواتير والمعاملات المالية) للمدة النظامية فقط، ثم تُحذف نهائياً.',
      en: 'We may retain certain data that UAE law requires us to keep (such as invoice and financial transaction records) for the statutory period only, after which it is permanently deleted.',
    },
    contact: {
      ar: 'للاستفسارات حول حذف البيانات: ',
      en: 'For data deletion inquiries: ',
    },
  },

  // ─────────────────────────────────────────────────────────────
  // 4) الدعم والتواصل / Support & Contact
  // ─────────────────────────────────────────────────────────────
  support: {
    title: {
      ar: 'مركز الدعم',
      en: 'Support Center',
    },
    subtitle: {
      ar: 'اختر الطريقة المناسبة للتواصل معنا — نحن هنا لمساعدتك',
      en: 'Choose your preferred way to reach us — we\'re here to help',
    },
    intro: {
      ar: 'فريق دعم CloudGM جاهز لخدمتك على مدار الساعة. اختر أحد الخيارات التالية للحصول على المساعدة الفورية:',
      en: 'The CloudGM support team is ready to help you 24/7. Choose one of the following options for instant assistance:',
    },
    options: [
      {
        // 🤖 محادثة مع الذكاء الاصطناعي — الخيار الأسرع
        key: 'ai_chat',
        iconKey: 'Bot',
        color: 'blue',
        highlight: true, // يبرز كخيار موصى به
        actionType: 'route',
        action: '/services/visa',
        ar: {
          title: 'المساعد الذكي',
          description: 'رد فوري خلال ثواني — اختر خدمتك وابدأ المحادثة مع مساعد CloudGM الذكي',
          cta: 'ابدأ المحادثة الآن',
          badge: 'الأسرع',
        },
        en: {
          title: 'AI Assistant',
          description: 'Instant reply within seconds — pick your service and start chatting with the CloudGM AI',
          cta: 'Start Chat Now',
          badge: 'Fastest',
        },
      },
      {
        // 💬 واتساب
        key: 'whatsapp',
        iconKey: 'MessageCircle',
        color: 'green',
        actionType: 'external',
        // {WHATSAPP} يُستبدل تلقائياً من COMPANY_INFO.social.whatsapp
        action: 'https://wa.me/{WHATSAPP}',
        ar: {
          title: 'واتساب',
          description: 'تواصل مباشر مع فريق الدعم عبر الواتساب',
          cta: 'فتح المحادثة على واتساب',
          badge: 'خلال ساعات العمل',
        },
        en: {
          title: 'WhatsApp',
          description: 'Chat directly with our support team on WhatsApp',
          cta: 'Open Chat on WhatsApp',
          badge: 'During business hours',
        },
      },
      {
        // ✉️ البريد الإلكتروني
        key: 'email',
        iconKey: 'Mail',
        color: 'sky',
        actionType: 'mailto',
        // {EMAIL} يُستبدل تلقائياً من COMPANY_INFO.email
        action: 'mailto:{EMAIL}',
        ar: {
          title: 'البريد الإلكتروني',
          description: 'أرسل لنا رسالة تفصيلية وسنرد عليك خلال 24 ساعة',
          cta: 'مراسلة الدعم',
          badge: 'رد خلال 24 ساعة',
        },
        en: {
          title: 'Email',
          description: 'Send us a detailed message and we\'ll reply within 24 hours',
          cta: 'Contact Support',
          badge: 'Reply within 24h',
        },
      },
    ],
    footer: {
      ar: 'أوقات عمل الدعم: من الأحد إلى الخميس، 9 صباحاً - 6 مساءً (بتوقيت الإمارات).',
      en: 'Support hours: Sunday to Thursday, 9:00 AM - 6:00 PM (UAE time).',
    },
  },
};

/**
 * استبدال متغيرات {EMAIL} و {PHONE} و {WHATSAPP} داخل النصوص بقيم COMPANY_INFO.
 * whatsapp = رقم مُنظّف بدون علامة + أو مسافات (للاستخدام في wa.me/...)
 */
export function fillPlaceholders(text, { email, phone, whatsapp } = {}) {
  if (!text) return '';
  return text
    .replaceAll('{EMAIL}', email || '')
    .replaceAll('{PHONE}', phone || '')
    .replaceAll('{WHATSAPP}', whatsapp || '');
}
